import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import CBadge from '@/components/shared/CBadge';
import type { Tag } from '@/api/typings/tags';


export default /*#__PURE__*/_defineComponent({
  __name: 'TagsCell',
  props: {
    tags: null
  },
  emits: ["manage"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'manage'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    tags: Tag[];
};





// Matches the .mr-1 utility class applied to each badge
const GAP_PX = 4;

const containerRef = ref<HTMLElement | null>(null);
const measureRef = ref<HTMLElement | null>(null);
const addButtonRef = ref<HTMLElement | null>(null);
const moreBadgeRef = ref<HTMLElement | null>(null);

const visibleCount = ref(props.tags.length);
const visibleTags = computed(() => props.tags.slice(0, visibleCount.value));
const hiddenCount = computed(() => props.tags.length - visibleTags.value.length);
// Set only when the first tag's normal (CSS-capped) width would leave no room for the
// "+ Tag" button, so it gets shrunk further just for this cell - see recalculateVisibleCount
const firstTagMaxWidthPx = ref<number | null>(null);

// Measured against the <td>, not the flex container itself - the container shrinks to fit
// however many tags we decide to show, so measuring it would make the budget shrink along with
// our own corrections. The <td>'s width is fixed by the table's column layout instead.
const getAvailableWidth = (): number | null => {
    const td = containerRef.value?.closest('td');
    if (!td) return null;

    const style = getComputedStyle(td);
    const paddingX = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0');
    return td.clientWidth - paddingX;
};

const recalculateVisibleCount = () => {
    const measure = measureRef.value;
    const available0 = getAvailableWidth();
    if (!measure || available0 === null || props.tags.length === 0) {
        visibleCount.value = props.tags.length;
        firstTagMaxWidthPx.value = null;
        return;
    }

    const measureBadgeEls = Array.from(measure.querySelectorAll<HTMLElement>('.tag-badge'));
    // Clear any shrink forced by a previous run before remeasuring - the available width may
    // have grown since then (e.g. the tags list changed, or the column was resized)
    if (measureBadgeEls[0]) measureBadgeEls[0].style.maxWidth = '';
    // badgeWidths[i] already includes each badge's own trailing .mr-1 gap, so that gap doubles
    // as "the gap before whatever comes next" - no extra gap needs reserving here
    const badgeWidths = measureBadgeEls.map((el) => el.offsetWidth + GAP_PX);
    const buttonWidth = addButtonRef.value?.offsetWidth ?? 0;
    const available = available0 - buttonWidth;
    const total = badgeWidths.reduce((sum, width) => sum + width, 0);

    if (total <= available) {
        firstTagMaxWidthPx.value = null;
        visibleCount.value = badgeWidths.length;
        return;
    }

    // Doesn't all fit, so hiding at least one tag (and therefore showing "+N") is unavoidable -
    // unless there's only one tag to begin with, reserve room for it up front
    const moreWidth = (moreBadgeRef.value?.offsetWidth ?? 0) + GAP_PX;
    const reserveForMore = badgeWidths.length > 1 ? moreWidth : 0;
    const firstTagBudget = available - reserveForMore;

    // Always keep at least the first tag (its own ellipsis handles a long name), but its normal
    // CSS-capped width can still be more than firstTagBudget on its own (e.g. a near-max-length
    // name with the "+ Tag" button - and maybe "+N" - reserved right after it), so shrink it
    // further, just for this cell, until it actually leaves room for what comes next.
    if (badgeWidths[0] > firstTagBudget) {
        const firstEl = measureBadgeEls[0];
        const shrunkWidth = Math.max(24, firstTagBudget - GAP_PX);
        firstEl.style.maxWidth = `${shrunkWidth}px`;
        badgeWidths[0] = firstEl.offsetWidth + GAP_PX;
        firstTagMaxWidthPx.value = shrunkWidth;
    } else {
        firstTagMaxWidthPx.value = null;
    }

    let used = badgeWidths[0];
    let count = 1;
    for (let i = 1; i < badgeWidths.length; i++) {
        if (used + badgeWidths[i] + moreWidth > available) break;
        used += badgeWidths[i];
        count++;
    }
    visibleCount.value = count;
};

let resizeObserver: ResizeObserver | undefined;

onMounted(async () => {
    await nextTick();
    recalculateVisibleCount();

    // The column can still be wider than its final width at this point - table auto-layout
    // sizes it to the widest cell, and sibling rows may not have trimmed their own tags yet.
    // Re-run once the <td> actually settles to its final width.
    const td = containerRef.value?.closest('td');
    if (td) {
        resizeObserver = new ResizeObserver(() => recalculateVisibleCount());
        resizeObserver.observe(td);
    }
});

onBeforeUnmount(() => resizeObserver?.disconnect());

watch(
    () => props.tags,
    async () => {
        await nextTick();
        recalculateVisibleCount();
    }
);

return { __sfc: true,props, emit, GAP_PX, containerRef, measureRef, addButtonRef, moreBadgeRef, visibleCount, visibleTags, hiddenCount, firstTagMaxWidthPx, getAvailableWidth, recalculateVisibleCount, resizeObserver, CBadge }
}

})